import { Injectable, OnDestroy } from '@angular/core';
import { HubConnection } from '@microsoft/signalr';
import { hubEvent } from '@shared/constants/hub-event';
import { ICameraPreview } from '@shared/interfaces/camera-preview';
import { IDevice } from '@shared/interfaces/device/device';
import { IDeviceHeatingProgress } from '@shared/interfaces/device/device-heating-progress';
import { IDeviceJob } from '@shared/interfaces/device/device-job';
import { IDeviceJobPublished } from '@shared/interfaces/device/device-job-published';
import { IDevicePublished } from '@shared/interfaces/device/device-published';
import { IDeviceStatus } from '@shared/interfaces/device/device-status';
import { IDeviceStatusPublished } from '@shared/interfaces/device/device-status-published';
import { IDeviceUploadingFileProgress } from '@shared/interfaces/device/device-uploading-file-progress';
import { ITeamDeviceStatus } from '@shared/interfaces/team/team-device-status';
import { map, Observable, Subject, filter, Subscription } from 'rxjs';
import { ITimelineDevice } from '@shared/interfaces/timeline/timeline-device';
import { SignalRService } from '../signal-r.service';
import { IDeviceJobRatingPublished } from '@shared/interfaces/device/device-job-rating-published';
import { IDeviceParsingProgress } from '@shared/interfaces/device/device-parsing-progress';
import { IDeviceFileListUpdatePublished } from '@shared/interfaces/device/device-fileListUpdate-published';
import { IExtruderPosition } from '@shared/interfaces/extruder-position';
import { AlertService } from '../alert.service';
import { IRefreshedNotificationItem } from '@notifications/abstract/refreshed-notification-item';
import { RefreshNotificationItemAction } from '@notifications/constants/refresh-notification-item-action';
import { IDeviceNotificationModel } from '@notifications/abstract/device-notification-model';
import dayjs from 'dayjs';
import { DeviceNotificationType } from '@notifications/constants/device-notification-type';
import { IDeviceProcess } from '@shared/interfaces/device/device-process';

@Injectable({
  providedIn: 'root',
})
export class DevicePublisherService implements OnDestroy {
  private subscriptions = new Subscription();

  private deviceStatusPublished$ = new Subject<IDeviceStatusPublished>();
  private deviceJobPublished$ = new Subject<IDeviceJobPublished>();
  private deviceJobRatingPublished$ = new Subject<IDeviceJobRatingPublished>();
  private deviceHeatingProgressPublished$ =
    new Subject<IDeviceProcess>();
  private deviceUploadingFileProgressPublished$ =
    new Subject<IDeviceProcess>();
  private deviceParsingProgressPublished$ =
    new Subject<IDeviceProcess>();
  private deviceFileListUpdatePublished$ =
    new Subject<IDeviceFileListUpdatePublished>();
  private deviceCameraPublished$ = new Subject<ICameraPreview>();
  private deviceExtruderPosition$ = new Subject<IExtruderPosition>();
  private devicePublished$ = new Subject<IDevicePublished>();
  private deviceMessagePublished$ = new Subject<
    IRefreshedNotificationItem<IDeviceNotificationModel>
  >();

  constructor(
    private readonly signalRService: SignalRService,
    private readonly alertService: AlertService
  ) {
    this.subscriptions.add(
      this.signalRService.hubConnection$.subscribe((hubConnection) => {
        if (hubConnection) this.subscribeEvents(hubConnection);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  private subscribeEvents(hubConnection: HubConnection): void {
    hubConnection.on(
      hubEvent.refreshDeviceStatus,
      (deviceStatusPublished: IDeviceStatusPublished) => {
        this.deviceStatusPublished$.next(deviceStatusPublished);
      }
    );

    hubConnection.on(
      hubEvent.refreshDeviceJob,
      (deviceJobPublished: IDeviceJobPublished) => {
        this.deviceJobPublished$.next(deviceJobPublished);
      }
    );

    hubConnection.on(
      hubEvent.refreshDeviceJobRating,
      (deviceJobRatingPublished: IDeviceJobRatingPublished) => {
        this.deviceJobRatingPublished$.next(deviceJobRatingPublished);
      }
    );

    hubConnection.on(
      hubEvent.refreshDeviceHeatingProgress,
      (deviceHeatingProgressPublished: IDeviceProcess) => {
        this.deviceHeatingProgressPublished$.next(
          deviceHeatingProgressPublished
        );
      }
    );

    hubConnection.on(
      hubEvent.refreshDeviceUploadingFileProgress,
      (deviceUploadingFileProgressPublished: IDeviceProcess) => {
        this.deviceUploadingFileProgressPublished$.next(
          deviceUploadingFileProgressPublished
        );
      }
    );

    hubConnection.on(
      hubEvent.refreshDeviceParsingProgress,
      (deviceParsingProgressPublished: IDeviceProcess) => {
        this.deviceParsingProgressPublished$.next(
          deviceParsingProgressPublished
        );
      }
    );

    hubConnection.on(
      hubEvent.refreshFileList,
      (fileListUpdate: IDeviceFileListUpdatePublished) => {
        this.deviceFileListUpdatePublished$.next(fileListUpdate);
      }
    );

    hubConnection.on(
      hubEvent.refreshCameraPreview,
      (deviceCameraPreviewPublished) => {
        this.deviceCameraPublished$.next(deviceCameraPreviewPublished);
      }
    );

    hubConnection.on(
      hubEvent.refreshExtruderPosition,
      (deviceExtruderPositionPublished) => {
        this.deviceExtruderPosition$.next(deviceExtruderPositionPublished);
      }
    );

    hubConnection.on(hubEvent.addDevice, (devicePublished) => {
      this.devicePublished$.next(devicePublished);
    });

    hubConnection.on(hubEvent.displayDeviceMessage, (deviceMessage) => {
      this.deviceMessagePublished$.next({
        action: RefreshNotificationItemAction.Prepend,
        value: deviceMessage,
      } as IRefreshedNotificationItem<IDeviceNotificationModel>);

      switch (deviceMessage.type) {
        case DeviceNotificationType.INFO:
          this.alertService.info(deviceMessage.message);
          break;
        case DeviceNotificationType.WARNING:
          this.alertService.warning(deviceMessage.message);
          break;
        case DeviceNotificationType.ERROR:
          this.alertService.danger(deviceMessage.message);
          break;
      }
    });
  }

  getDeviceStatusObservable(): Observable<{
    deviceId: string;
    deviceStatus: IDeviceStatus;
  }> {
    return this.deviceStatusPublished$.pipe(
      map((dsp) => {
        return {
          deviceId: dsp.deviceId,
          deviceStatus: {
            statusCode: dsp.statusCode,
            isLocalized: dsp.isLocalized,
            firmwareName: dsp.firmwareName,
            lifeTime: dsp.lifeTime,
            maintenanceRequired: dsp.maintenanceRequired,
            firmwareNewVersionAvailable: dsp.firmwareNewVersionAvailable,
            currentPrintJobId: dsp.currentPrintJobId,
            reason: dsp.reason,
            heatingProgress: dsp.heatingProgress,
            wifiIp: dsp.wifiIp,
            ethernetIp: dsp.ethernetIp,
            currentLoadedMaterials: dsp.currentLoadedMaterials,
            maintenanceInfo: dsp.maintenanceInfo,
            memory: dsp.memory,
            chamber: dsp.chamber,
            extruders: dsp.extruders,
            film: dsp.film,
          } as IDeviceStatus,
        };
      })
    );
  }

  getDeviceJobObservable(): Observable<{
    deviceId: string;
    deviceJob: IDeviceJob;
  }> {
    return this.deviceJobPublished$.pipe(
      map((djp) => {
        return {
          deviceId: djp.deviceId,
          deviceJob: {
            fileName: djp.fileName,
            userName: djp.userName,
            startDate: djp.startDate,
            totalPrintTime: djp.totalPrintTime,
            currentLayer: djp.currentLayer,
            layersCount: djp.layersCount,
            leftTime: djp.leftTime,
            progress: djp.progress,
            preview: djp.preview,
            materials: djp.materials,
            pauses: djp.pauses,
            id: djp.id,
            endDate: djp.endDate,
            reason: djp.reason,
            profile: djp.profile
          } as IDeviceJob,
        };
      })
    );
  }

  getDeviceJobsRatingObservable() {
    return this.deviceJobRatingPublished$;
  }

  getTeamDeviceStatusObservable(): Observable<{
    deviceId: string;
    teamDeviceStatus: ITeamDeviceStatus;
  }> {
    return this.deviceStatusPublished$.pipe(
      map((dsp) => {
        return {
          deviceId: dsp.deviceId,
          teamDeviceStatus: {
            statusCode: dsp.statusCode,
            reason: dsp.reason,
            heatingProgress: dsp.heatingProgress,
          } as ITeamDeviceStatus,
        };
      })
    );
  }

  getDeviceHeatingProgressObservable(deviceId: string): Observable<number> {
    return this.deviceHeatingProgressPublished$.pipe(
      filter((p) => p.deviceId === deviceId),
      map((p) => p.progress)
    );
  }

  getDeviceParsingProgressObservable(deviceId: string): Observable<IDeviceProcess> {
    return this.deviceParsingProgressPublished$.pipe(
      filter((p) => p.deviceId === deviceId)
    );
  }

  getDeviceUploadingProgressObservable(
    deviceId: string,
    uploadId: string
  ): Observable<IDeviceUploadingFileProgress> {
    return this.deviceUploadingFileProgressPublished$.pipe(
      filter((p) => p.deviceId === deviceId && p.id === uploadId)
    );
  }

  getDeviceRefreshFileListObservable(
    deviceId: string
  ): Observable<IDeviceFileListUpdatePublished> {
    return this.deviceFileListUpdatePublished$.pipe(
      filter((p) => p.deviceId == deviceId)
    );
  }

  getCameraPreviewObservable(deviceId: string) {
    return this.deviceCameraPublished$.pipe(
      filter((p) => p.deviceId === deviceId)
    );
  }

  getCameraPreviewsObservable() {
    return this.deviceCameraPublished$;
  }

  getDeviceMessageObservable() {
    return this.deviceMessagePublished$;
  }

  getExtruderPositionObservable(deviceId: string) {
    return this.deviceExtruderPosition$.pipe(
      filter((p) => p.deviceId === deviceId)
    );
  }

  getDeviceObservable(): Observable<{ groupId: string; device: IDevice }> {
    return this.devicePublished$.pipe(
      map((dp) => {
        return {
          groupId: dp.groupId,
          device: {
            id: dp.id,
            name: dp.name,
            serialNumber: dp.serialNumber,
            ownerId: dp.ownerId,
            typeId: dp.typeId,
            description: dp.description,
            picture: dp.picture,
            type: dp.type,
            hasCameraFeature: dp.hasCameraFeature,
            supportLiveStream: dp.supportLiveStream,
            status: dp.status,
            job: dp.job,
          } as IDevice,
        };
      })
    );
  }

  getTimelineDeviceObservable(): Observable<{
    groupId: string;
    device: ITimelineDevice;
  }> {
    return this.devicePublished$.pipe(
      map((dp) => {
        return {
          groupId: dp.groupId,
          device: {
            id: dp.id,
            name: dp.name,
            picture: dp.picture,
            serialNumber: dp.serialNumber,
            type: dp.type,
            status: {
              statusCode: dp.status.statusCode,
              reason: dp.status.reason,
              heatingProgress: dp.status.heatingProgress,
            },
          memory: {
            total: dp.status.memory.total,
            used: dp.status.memory.used
          },
          jobs: [],
          isSelected: false
          } as ITimelineDevice,
        };
      })
    );
  }
}
